import getApiResponseData from '@utils/get-api-response-data'

import { api, type Api } from './api'
import { toFulfillment, toFulfillmentCity } from './lib/to-fulfillment'
import { toFulfillmentDetail } from './lib/to-fulfillment-detail'

export const fetchFulfillmentList = async (params: Api.ListParams) => {
  const { data } = await api.list(params)
  const result = getApiResponseData(data)

  if (!result) {
    throw new Error('Fulfillment list response has no data')
  }

  return {
    items: result.items.map(toFulfillment),
    pagination: result.pagination,
    counts: result.counts,
  }
}

export const fetchFulfillmentCities = async (params: Api.CityOptionsParams) => {
  const { data } = await api.cityOptions(params)
  const result = getApiResponseData(data)

  if (!result) {
    throw new Error('Fulfillment city response has no data')
  }

  return {
    cities: result.cities.map(toFulfillmentCity),
    ipCity: result.ip_city ? toFulfillmentCity(result.ip_city) : null,
  }
}

export const fetchFulfillmentCity = async (slug: string) => {
  const { data } = await api.city(slug)
  const result = getApiResponseData(data)

  if (!result) {
    throw new Error('Fulfillment city page response has no data')
  }

  return {
    city: toFulfillmentCity(result.city),
    shipmentCounts: result.shipment_counts,
    relatedCities: result.related_cities.map(toFulfillmentCity),
  }
}

/** Свои карточки со сводкой. Гость получает 401, поэтому вызывающий код готов к пустому списку. */
export const fetchMyFulfillments = async () => {
  const { data } = await api.myList()

  return getApiResponseData(data)?.items ?? []
}

export const fetchFulfillmentDetail = async (slug: string) => {
  const { data } = await api.detail(slug)
  const result = getApiResponseData(data)

  if (!result) {
    throw new Error('Fulfillment detail response has no data')
  }

  return toFulfillmentDetail(result)
}

/** Сколько городов помещается в один запрос справочника: предел задан на стороне сервера. */
const cityRequestLimit = 100

/**
 * Числа каталога для страниц-приглашений.
 *
 * `null` означает не «ноль», а «спросить не удалось»: страницы показывают в этом случае не
 * счётчик, а факт о сервисе. Иначе упавший сервер выглядел бы как пустой каталог.
 */
export type FulfillmentCatalogStats = {
  fulfillmentCount: number | null
  cityCount: number | null
}

/**
 * Сколько карточек в каталоге и сколько в нём городов. Отказ любого из двух запросов не роняет
 * страницу: он записывается в журнал сервера и превращается в `null`.
 */
export const fetchFulfillmentCatalogStats = async (): Promise<FulfillmentCatalogStats> => {
  const [list, cities] = await Promise.allSettled([
    fetchFulfillmentList({ page: 1, per_page: 1 }),
    fetchFulfillmentCities({ limit: cityRequestLimit }),
  ])

  if (list.status === 'rejected') {
    console.error('Fulfillment catalog stats: list', list.reason)
  }

  if (cities.status === 'rejected') {
    console.error('Fulfillment catalog stats: cities', cities.reason)
  }

  return {
    fulfillmentCount: list.status === 'fulfilled' ? list.value.pagination.total : null,
    cityCount: cities.status === 'fulfilled' ? cities.value.cities.length : null,
  }
}

export { MyFulfillmentStatusBadge } from './ui/my-fulfillment-status-badge'
export { type Api as FulfillmentApi } from './api'
